// src/utils/ficheFnct.ts — SCE Issues
// Ce que la fiche FNC-T dit d'elle-même (refonte du 12/10). Pur, testable.
// Maquette : https://claude.ai/artifact/M5GHzpFzqJQ3kmh8neSXRr
//
// La fiche suit le cycle de vie de la FNC-T : quatre étapes (détection,
// correction, analyse et prévention facultative, clôture), UNE phrase pour la
// prochaine étape, une liste de contrôle de clôture, et l'état du dernier
// rapport déposé (provisoire tant que la FNC-T est ouverte, final ensuite).
//
// Retours de Guillaume (12/10) :
//  - le N° est attribué dès l'enregistrement (la détection peut durer) ;
//  - le rapport se génère dès que les actions principales sont posées
//    (provisoire), puis à la clôture (final) ;
//  - « Conséquence » est en réalité la colonne « Priority explanation » de la
//    base : elle justifie la priorité, reste interne, et n'est affichée que si
//    elle dit autre chose que les documents impactés (doublon fréquent).

import type { ActionFnct, EvenementFnct, Fnct, Jour } from '@/types/fnct.types'
import { LIEUX_FNCT, PRIORITES_FNCT } from '@/config/fnct.config'
import { criticiteRpn, debutFnct, estEnReleve, jourLisible, jourParis, joursEntre, rpn, statutAction } from './fnct'
import { nomCourt } from './actionsFnct'

// ─── Libellés français des valeurs de la base ───

const LEGITIMITES: Record<string, string> = { Accepted: 'acceptée', 'Not accepted': 'non acceptée', Goodwill: 'geste commercial' }
const CRITICITES: Record<string, string> = { Minor: 'Mineure', Major: 'Majeure', Critical: 'Critique' }

export function legitimiteFr(v: string): string {
  return LEGITIMITES[v.trim()] ?? v.trim()
}

export function criticiteFr(v: string): string {
  return CRITICITES[v] ?? v
}

export function lieuFr(v: string): string {
  return LIEUX_FNCT.find((l) => l.valeur === v)?.libelle ?? v
}

const ANALYSES: Record<string, string> = { 'Not required': 'Non requise', 'Required-New': 'Requise (nouvelle)', 'Required-Ongoing': 'Requise (en cours)' }

/** « Analyse de risque (AQ Opérations) » de la base, en français. */
export function analyseRisqueFr(v: string): string {
  return ANALYSES[v.trim()] ?? v.trim()
}

export function prioriteFr(v: string): string {
  return PRIORITES_FNCT.find((p) => p.valeur === v)?.libelle ?? v
}

function pluriel(n: number, mot: string, motPluriel = `${mot}s`): string {
  return `${n} ${n > 1 ? motPluriel : mot}`
}

function jourCourt(j: Jour | null | undefined): string {
  return jourLisible(j).slice(0, 5)
}

// ─── Les actions, vues par la fiche ───

/** Correction : CO, et les actions sans catégorie (base Excel). */
export function estCorrection(a: Pick<ActionFnct, 'categorie'>): boolean {
  return a.categorie === 'CO' || a.categorie === ''
}

function enRetard(a: ActionFnct, aujourdhui: Jour): boolean {
  return statutAction(a, aujourdhui) === 'en-retard'
}

/** Jours de retard d'une action ouverte (0 si elle ne l'est pas). */
export function joursDeRetard(a: Pick<ActionFnct, 'echeance' | 'echeanceReportee' | 'cloture'>, aujourdhui: Jour): number {
  const limite = a.echeanceReportee ?? a.echeance
  if (a.cloture || !limite) return 0
  return Math.max(0, joursEntre(limite, aujourdhui))
}

function derniereCloture(actions: readonly ActionFnct[]): Jour | null {
  let d: Jour | null = null
  for (const a of actions) if (a.cloture && (!d || a.cloture > d)) d = a.cloture
  return d
}

/** Toutes les actions closes (et il y en a) : la FNC-T peut passer en revue de clôture. */
export function pretAClore(f: Pick<Fnct, 'cloture'>, actions: readonly ActionFnct[]): boolean {
  return f.cloture === null && actions.length > 0 && actions.every((a) => a.cloture)
}

// ─── Où en est-on : les quatre étapes ───

export type EtatEtape = 'fait' | 'close' | 'retard' | 'encours' | 'avenir' | 'facultatif'

export interface EtapeFnct {
  titre: string
  l1: string
  l2: string
  etat: EtatEtape
  /** La deuxième ligne en rouge (un retard). */
  alerte?: boolean
}

function etapeActions(
  titre: string,
  actions: readonly ActionFnct[],
  aujourdhui: Jour,
  vide: { l1: string; l2: string; etat: EtatEtape },
): EtapeFnct {
  if (actions.length === 0) return { titre, ...vide }
  const closes = actions.filter((a) => a.cloture).length
  const l1 = `${closes} ${closes > 1 ? 'actions' : 'action'} sur ${actions.length} ${closes > 1 ? 'closes' : 'close'}`
  if (closes === actions.length) return { titre, l1, l2: `Terminée le ${jourLisible(derniereCloture(actions))}`, etat: 'fait' }
  const retards = actions.filter((a) => enRetard(a, aujourdhui)).length
  if (retards > 0) return { titre, l1, l2: `${retards} en retard`, etat: 'retard', alerte: true }
  const prochaine = actions
    .filter((a) => !a.cloture)
    .map((a) => a.echeanceReportee ?? a.echeance)
    .filter((j): j is Jour => Boolean(j))
    .sort()[0]
  return { titre, l1, l2: prochaine ? `Prochaine échéance le ${jourLisible(prochaine)}` : 'Échéances à fixer', etat: 'encours' }
}

const NOMS_PREVENTION: readonly [ActionFnct['categorie'], string, string][] = [
  ['RCA', 'analyse des causes', 'analyses des causes'],
  ['CA', 'action corrective', 'actions correctives'],
  ['PA', 'action préventive', 'actions préventives'],
  ['OA', 'opportunité', 'opportunités'],
]

/** « 2 actions correctives, 1 préventive ». */
export function compositionPrevention(actions: readonly Pick<ActionFnct, 'categorie'>[]): string {
  const morceaux: string[] = []
  for (const [c, un, plusieurs] of NOMS_PREVENTION) {
    const n = actions.filter((a) => a.categorie === c).length
    // Après « actions correctives », « 1 préventive » suffit.
    const nom = c === 'PA' && morceaux.some((x) => x.includes('corrective')) ? (n > 1 ? 'préventives' : 'préventive') : n > 1 ? plusieurs : un
    if (n > 0) morceaux.push(`${n} ${nom}`)
  }
  const texte = morceaux.join(', ')
  return texte.charAt(0).toUpperCase() + texte.slice(1)
}

/**
 * Un libellé repris en milieu de phrase (« Le 08/04/2026, test (interne) ») : sa
 * première lettre passe en minuscule, sauf si le premier mot est un sigle —
 * « FAT (interne) », « SAT », « IQ / OQ » gardent leurs capitales (« fAT », 22/10).
 */
export function enCoursDePhrase(texte: string): string {
  const premier = texte.trim().split(/[\s/(]/)[0] ?? ''
  if (premier.length > 1 && /[A-Z]/.test(premier) && premier === premier.toUpperCase()) return texte
  return texte.charAt(0).toLowerCase() + texte.slice(1)
}

export function etapesFnct(f: Fnct, actions: readonly ActionFnct[], aujourdhui: Jour): EtapeFnct[] {
  const lieu = f.lieuDetection ? lieuFr(f.lieuDetection) : ''
  const detection: EtapeFnct = {
    titre: 'Détection',
    l1: [f.dateDetection ? `Le ${jourLisible(f.dateDetection)}` : '', lieu ? enCoursDePhrase(lieu) : '']
      .filter(Boolean)
      .join(', ') || 'Date non renseignée',
    l2: `N° attribué${f.enregistrement ? ` le ${jourCourt(f.enregistrement)}` : ''}${f.enregistrePar ? `, par ${nomCourt(f.enregistrePar)}` : ''}`,
    etat: 'fait',
  }
  // Sans action CO, la date « correction close » de la base dit que la correction est faite
  // (FNC-T_2024-1 : seulement des CA et une PA, correction close le 16/01/2024).
  const correction = etapeActions(
    'Correction',
    actions.filter(estCorrection),
    aujourdhui,
    f.clotureCorrection
      ? { l1: 'Correction close', l2: `Le ${jourLisible(f.clotureCorrection)}`, etat: 'fait' }
      : { l1: 'Aucune action de correction', l2: 'À définir', etat: f.cloture ? 'fait' : 'encours' },
  )
  const prevention = actions.filter((a) => !estCorrection(a))
  const analyse = etapeActions('Analyse et prévention', prevention, aujourdhui, { l1: 'Aucune action RCA, CA ou PA', l2: 'Facultative', etat: 'facultatif' })
  // Une fois faite, elle se dit par sa composition (« 2 actions correctives, 1 préventive »).
  if (analyse.etat === 'fait') {
    analyse.l1 = compositionPrevention(prevention)
    const tard = prevention.filter((a) => statutAction(a, aujourdhui) === 'terminee-en-retard').length
    analyse.l2 = tard === 0 ? (prevention.length > 1 ? 'Toutes closes à temps' : 'Close à temps') : `${tard} close${tard > 1 ? 's' : ''} en retard`
  }
  const leg = f.legitimite ? `Légitimité : ${legitimiteFr(f.legitimite)}` : 'Légitimité à choisir'
  const ouvertes = actions.filter((a) => !a.cloture).length
  const cloture: EtapeFnct = f.cloture
    ? { titre: 'Clôture', l1: `Close le ${jourLisible(f.cloture)}`, l2: leg, etat: 'close' }
    : pretAClore(f, actions)
      ? { titre: 'Clôture', l1: leg, l2: 'Puis clore', etat: 'encours' }
      : {
          titre: 'Clôture',
          l1: leg,
          l2: ouvertes > 0 ? `Après ${ouvertes > 1 ? `les ${ouvertes} actions ouvertes` : 'l’action ouverte'}` : 'Après les actions',
          etat: 'avenir',
        }
  return [detection, correction, analyse, cloture]
}

// ─── L'état en une pastille, et la prochaine étape en une phrase ───

/**
 * Ancienneté et durée comptées depuis l'OUVERTURE (14/10) : un relevé d'écarts peut
 * attendre des semaines avant d'être ouvert. Sans ouverture : l'enregistrement.
 */
export function statutFnct(
  f: Fnct,
  actions: readonly ActionFnct[],
  aujourdhui: Jour,
): { texte: string; etat: 'releve' | 'encours' | 'prete' | 'close' } {
  const debut = debutFnct(f)
  if (f.cloture) {
    const n = debut ? joursEntre(debut, f.cloture) : null
    return { texte: `Close le ${jourLisible(f.cloture)}${n !== null ? `, en ${pluriel(n, 'jour')}` : ''}`, etat: 'close' }
  }
  if (estEnReleve(f)) {
    const n = debut ? joursEntre(debut, aujourdhui) : null
    return { texte: `En relevé${debut ? ` depuis le ${jourCourt(debut)}` : ''}${n !== null ? ` · ${pluriel(n, 'jour')}` : ''}`, etat: 'releve' }
  }
  if (pretAClore(f, actions)) return { texte: 'Prête à clore', etat: 'prete' }
  const n = debut ? joursEntre(debut, aujourdhui) : null
  return { texte: n !== null ? `En cours depuis ${pluriel(n, 'jour')}` : 'En cours', etat: 'encours' }
}

export interface ProchaineEtape {
  label: 'Prochaine étape' | 'Bilan'
  texte: string
  /** Section à atteindre (`plan`, `cloture`). */
  cible?: 'plan' | 'cloture'
  bouton?: string
  /** Bouton jaune : la seule chose à faire. */
  principal?: boolean
}

export function prochaineEtape(f: Fnct, actions: readonly ActionFnct[], aujourdhui: Jour): ProchaineEtape {
  if (f.cloture) {
    const debut = debutFnct(f)
    const n = debut ? joursEntre(debut, f.cloture) : null
    const tard = actions.filter((a) => statutAction(a, aujourdhui) === 'terminee-en-retard').length
    const morceaux = [
      n !== null ? `Close en ${pluriel(n, 'jour')}.` : 'Close.',
      actions.length === 0
        ? 'Aucune action.'
        : `${pluriel(actions.length, 'action')}, ${tard === 0 ? (actions.length > 1 ? 'toutes terminées à temps' : 'terminée à temps') : `dont ${tard} terminée${tard > 1 ? 's' : ''} en retard`}.`,
      f.legitimite ? `Légitimité ${legitimiteFr(f.legitimite)}.` : '',
    ]
    return { label: 'Bilan', texte: morceaux.filter(Boolean).join(' ') }
  }
  if (actions.length === 0) {
    return { label: 'Prochaine étape', texte: 'Aucune action pour l’instant : posez le plan d’actions.', cible: 'plan', bouton: 'Voir le plan' }
  }
  if (pretAClore(f, actions)) {
    const d = derniereCloture(actions)
    const depuis = d ? joursEntre(d, aujourdhui) : 0
    return {
      label: 'Prochaine étape',
      texte: `Tout est fait depuis le ${jourCourt(d)}${depuis > 0 ? `, il y a ${pluriel(depuis, 'jour')}` : ''}. ${
        f.legitimite ? 'Il reste à clore.' : 'Il reste à choisir la légitimité, puis à clore.'
      }`,
      cible: 'cloture',
      bouton: 'Clore la FNC-T',
      principal: true,
    }
  }
  const retards = actions.filter((a) => enRetard(a, aujourdhui))
  if (retards.length > 0) {
    const pire = Math.max(...retards.map((a) => joursDeRetard(a, aujourdhui)))
    const qui = [...new Set(retards.map((a) => a.responsable.trim()).filter(Boolean))]
    let texte: string
    if (retards.length === 1) texte = `L’action de ${qui[0] ?? 'son responsable'} est en retard de ${pluriel(pire, 'jour')}.`
    else if (qui.length === 1) texte = `Les ${retards.length} actions de ${qui[0]} sont en retard, la plus ancienne de ${pluriel(pire, 'jour')}.`
    else texte = `${retards.length} actions en retard (${qui.join(', ')}), la plus ancienne de ${pluriel(pire, 'jour')}.`
    return { label: 'Prochaine étape', texte, cible: 'plan', bouton: retards.length > 1 ? 'Voir ces actions' : 'Voir l’action' }
  }
  const suivante = actions
    .filter((a) => !a.cloture)
    .sort((x, y) => (x.echeanceReportee ?? x.echeance ?? '9999').localeCompare(y.echeanceReportee ?? y.echeance ?? '9999'))[0]
  const echeance = suivante ? (suivante.echeanceReportee ?? suivante.echeance) : null
  if (!suivante || !echeance) return { label: 'Prochaine étape', texte: 'Des actions attendent leur échéance.', cible: 'plan', bouton: 'Voir le plan' }
  const dans = joursEntre(aujourdhui, echeance)
  return {
    label: 'Prochaine étape',
    texte: `Prochaine échéance le ${jourCourt(echeance)} (${dans === 0 ? 'aujourd’hui' : `dans ${pluriel(dans, 'jour')}`}) : « ${suivante.description} », ${suivante.responsable}.`,
    cible: 'plan',
    bouton: 'Voir le plan',
  }
}

// ─── La clôture, en liste de contrôle ───

export interface ItemCloture {
  libelle: string
  detail: string
  /** ok : fait ; manque : bloque la clôture ; choisir : à faire en clôturant ; conseil : signalé, pas bloquant. */
  etat: 'ok' | 'manque' | 'choisir' | 'conseil'
}

export function itemsCloture(f: Fnct, actions: readonly ActionFnct[], aujourdhui: Jour): ItemCloture[] {
  const items: ItemCloture[] = []
  if (f.cloture) items.push({ libelle: `Close le ${jourLisible(f.cloture)}`, detail: f.leader ? `Nom porté sur le rapport : ${f.leader}, leader` : '', etat: 'ok' })
  const closes = actions.filter((a) => a.cloture).length
  if (actions.length === 0) items.push({ libelle: 'Toutes les actions closes', detail: 'Aucune action', etat: f.cloture ? 'conseil' : 'manque' })
  else if (closes === actions.length)
    items.push({ libelle: 'Toutes les actions closes', detail: `${closes} sur ${actions.length} · la dernière le ${jourLisible(derniereCloture(actions))}`, etat: 'ok' })
  else {
    const retards = actions.filter((a) => enRetard(a, aujourdhui)).length
    items.push({ libelle: 'Toutes les actions closes', detail: `${closes} sur ${actions.length}${retards ? ` · ${retards} en retard` : ''}`, etat: 'manque' })
  }
  items.push(
    f.legitimite
      ? { libelle: 'Légitimité', detail: legitimiteFr(f.legitimite).replace(/^./, (c) => c.toUpperCase()), etat: 'ok' }
      : { libelle: 'Légitimité', detail: 'À choisir en clôturant', etat: 'choisir' },
  )
  const crit = criticiteRpn(f)
  const valeur = rpn(f)
  items.push(
    crit && valeur !== null
      ? { libelle: 'Risque évalué', detail: `${criticiteFr(crit)} · RPN ${valeur}`, etat: 'ok' }
      : { libelle: 'Risque évalué', detail: 'Non évalué : signalé, pas bloquant', etat: 'conseil' },
  )
  if (!f.cloture) items.push({ libelle: 'Rapport final', detail: 'À générer juste après la clôture : il porte la date et le nom de clôture.', etat: 'conseil' })
  return items
}

// ─── Le dernier rapport déposé ───

export interface DernierRapport {
  /** Jour du dépôt, à Pompey. */
  jour: Jour
  nom: string
  /** Adresse SharePoint du fichier déposé, s'il l'a été. */
  lien?: string
  /** Généré après la clôture : il porte la date et le nom de clôture. */
  final: boolean
  /** Actions closes après le dépôt : le rapport ne les montre pas closes. */
  closesDepuis: number
  /** Plus à jour : des actions closes depuis, ou la FNC-T close après un rapport provisoire. */
  perime: boolean
}

/**
 * Le dernier rapport, d'après l'historique (événement « Rapport » : nom du
 * fichier, puis son adresse SharePoint sur la ligne suivante).
 */
export function dernierRapport(f: Pick<Fnct, 'cloture'>, actions: readonly ActionFnct[], historique: readonly EvenementFnct[]): DernierRapport | null {
  const rapports = historique.filter((e) => e.type === 'Rapport').sort((x, y) => y.date.localeCompare(x.date))
  const r = rapports[0]
  if (!r) return null
  const d = new Date(r.date)
  if (Number.isNaN(d.getTime())) return null
  const jour = jourParis(d)
  const [nom = '', lien = ''] = r.contenu.split('\n').map((l) => l.trim())
  const final = f.cloture !== null && jour >= f.cloture
  const closesDepuis = actions.filter((a) => a.cloture && a.cloture > jour).length
  return {
    jour,
    nom,
    ...(lien.startsWith('http') ? { lien } : {}),
    final,
    closesDepuis,
    perime: closesDepuis > 0 || (f.cloture !== null && !final),
  }
}

// ─── Documents impactés et justification de la priorité ───

/** Mots qui ne disent rien de neuf : « Test 76968 is not compliant » se réduit à « 76968 ». */
const MOTS_VIDES = new Set(['a', 'an', 'the', 'and', 'or', 'of', 'on', 'is', 'are', 'not', 'isn', 'aren', 't', 's', 'be', 'compliant', 'test', 'tests'])

function motsUtiles(t: string): string[] {
  return t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((m) => m && !MOTS_VIDES.has(m))
}

/**
 * La justification de la priorité, si elle dit autre chose que les documents
 * impactés. Dans la base, elle les répète souvent (« Test 12895 not
 * compliant » des deux côtés, ou « Test 76968 is not compliant » sous « EDE is
 * not compliant (test 76968) ») : on ne l'affiche alors qu'une fois. Répéter,
 * c'est n'apporter aucun mot qui ne soit déjà dans les documents impactés.
 */
export function justificationDistincte(f: Pick<Fnct, 'documentsImpactes' | 'explicationPriorite'>): string {
  const j = f.explicationPriorite.trim()
  if (!j) return ''
  const docs = new Set(motsUtiles(f.documentsImpactes))
  const mots = motsUtiles(j)
  if (mots.every((m) => docs.has(m))) return ''
  return j
}

/** Titre de la fiche : la première ligne de la description ; le reste, en détail. */
export function titreEtDetails(description: string): { titre: string; details: string[] } {
  const lignes = description
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
  return { titre: lignes[0] ?? '', details: lignes.slice(1) }
}
